import { apiGet, apiPatch, API_BASE } from './api';

/**
 * The received-item mutations behind the line-item ticks (2026-08-15).
 *
 * ONE implementation of "tick an item / tick them all", shared by
 * `DepartmentOrders.js` and `MyOrders.js` — a FACTORY the page calls once,
 * handing over its own cache/refresh/error machinery, rather than a copy per
 * page. (A copied body instead of a call bit this project three times in one
 * day — 2026-08-11's method lessons — and these two pages would be the fourth.)
 *
 * The endpoints and the refresh sequence are carried over verbatim from the
 * retired "Items received" block in `OrderExpandedDetails.js`:
 *
 *   • Per-item  → `PATCH /api/orders/:orderId/items/:itemId/received`
 *                 with `{ is_received: !current }` (the FLIPPED value).
 *   • Tick-all  → `PATCH /api/orders/:orderId/items/receive-all`
 *                 with `{ is_received: next }`.
 *   • On success BOTH re-fetch the DETAIL (`GET /api/orders/:id`) and hand it
 *     to `onDetailRefreshed(orderId, freshDetail)` — the checkboxes and the
 *     fresh `can_edit` live on the page's detail cache — THEN call
 *     `onListRefresh(orderId)`: the collapsed row's Delivery Progress bar and
 *     status badge read the LIST payload's `delivery_info`/`status`, which the
 *     status auto-advance (partially_delivered/delivered) changes and a
 *     detail-only refresh would leave stale.
 *
 * 🔴 NO OPTIMISTIC UPDATE, deliberately — the row repaints only from what the
 * server answered, the same rule the notify flag records (`no_requester` must
 * not mark the line). 🔀 2026-10-28: a REFUSED PATCH now re-reads too (the
 * same detail-then-list refresh), THEN surfaces the error through `onError`
 * with the server's `code` and `status` on it — a 409 means the row was stale.
 *
 * `onBusyChange(orderId, busy)` brackets every mutation so the page can
 * disable that ORDER's checkboxes while a request is in flight (one shared
 * flag per order, like the retired block's `receiving` state — a second tick
 * during a bulk PATCH is a race, not a queue).
 *
 * 🔴 **`onDetailInvalidated(orderId)` (2026-08-27, W1) — "fail to EMPTY, never
 * fail to STALE".** The MUTATION (the PATCH above) can succeed while the
 * follow-up `GET /api/orders/:id` fails — a non-ok response or a dropped
 * connection. Until this optional callback existed, that failure was
 * swallowed silently: `onDetailRefreshed` was simply never called and the
 * page's detail cache kept whatever it held BEFORE the tick, with nothing
 * that could tell it was now wrong. Because `toggleExpand` on the order
 * pages only re-fetches a detail when the cache entry is EMPTY, a stale
 * entry blocks its own repair — a re-expand replays the same wrong state
 * forever, and (per the retired tri-state tick-all, computed from that
 * same cache) a subsequent Select-all click could send `receive-all` with the
 * WRONG target, un-receiving items an unrelated, successful mutation never
 * touched (server ruling F3: the un-receive-ALL direction clears every item
 * on the order regardless of who received it — see `routes/orders.js`'s
 * `receive-all` handler).
 *
 * This is byte-for-byte the shape `utils/refreshOneOrder.js`'s
 * `refreshOrderRow` closed the same day for the action-cluster pages: "the
 * cache delete is unconditional and first, fail to empty never fail to
 * stale". `onDetailInvalidated` is this file's half of that rule — it is
 * called exactly when `onDetailRefreshed` would NOT be, so a caller can drop
 * its cache entry (`delete orderDetails[orderId]`) the same way
 * `refreshOrderRow` does, and `toggleExpand`'s existing fetch-when-empty path
 * repairs it on the next re-expand with no new request added anywhere.
 *
 * 🔀 **WIRED** (this said "ADDITIVE AND CURRENTLY UNWIRED" until corrected
 * 2026-10-28, measured by grep): all three callers pass it —
 * `hooks/useDeptOrderExpansion.js` and `hooks/useMyOrderExpansion.js`
 * (`handleOrderDetailInvalidated`) and `pages/OrderPage.js`. Default `null`.
 *
 * 🔀 **`onError` is deliberately NOT called for a refresh-only failure,
 * which is a behaviour change from before this file's 2026-08-27 revision.**
 * The MUTATION succeeded; only the read-back afterwards failed. Surfacing
 * "Failed to update item" for that is a false alarm — the same call
 * `refreshOrderRow` makes for its own post-action refresh. `onError` still
 * fires, exactly as before, for a refused PATCH itself (§4).
 *
 * 🔀 **`onListRefresh` NOW FIRES ON A THROWN GET TOO — previously it did
 * not**, because the throw used to propagate straight out of this function,
 * skipping the line below it. It already fired unconditionally when the GET
 * merely *resolved* non-ok; catching the GET's own failure here (rather than
 * letting `run()`'s outer try/catch see it) makes the two failure shapes
 * symmetric and stops the LIST view going stale on a dropped connection too,
 * at no extra request — `onListRefresh` was always going to be called this
 * turn regardless of which branch got there.
 */
export function makeReceivedItemHandlers({
  onDetailRefreshed = null,
  onDetailInvalidated = null,
  onListRefresh = null,
  onBusyChange = null,
  onError = null,
}) {
  const refresh = async (orderId) => {
    if (onDetailRefreshed || onDetailInvalidated) {
      try {
        const res = await apiGet(`${API_BASE}/api/orders/${orderId}`);
        if (res.ok) {
          if (onDetailRefreshed) onDetailRefreshed(orderId, await res.json());
        } else if (onDetailInvalidated) {
          onDetailInvalidated(orderId);
        }
      } catch {
        // A dropped connection is the SAME "fail to empty" case as a
        // resolved non-ok response — see the docblock above. Caught here,
        // not left to propagate, so it cannot be mistaken for the MUTATION
        // having failed (see the `onError` note above) and so the
        // `onListRefresh` call below still runs on this failure shape too.
        if (onDetailInvalidated) onDetailInvalidated(orderId);
      }
    }
    if (onListRefresh) onListRefresh(orderId);
  };

  const run = async (orderId, mutate, failureText) => {
    if (onBusyChange) onBusyChange(orderId, true);
    try {
      const res = await mutate();
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.error || failureText);
        // The server's `code` (`order_changed`, `line_not_ordered`, …) and
        // status ride on the Error, so a caller can tell a race from a refusal.
        if (body.code) err.code = body.code;
        err.status = res.status;
        // 🔀 2026-10-28 (re-check MINOR): a REFUSED tick means the row on screen
        // is STALE (a 409 `order_changed` says exactly that) — re-read the
        // server's state before surfacing the error, or the control keeps
        // showing the state the server just refused until the next poll.
        await refresh(orderId);
        throw err;
      }
      await refresh(orderId);
    } catch (err) {
      if (onError) onError(err);
    } finally {
      if (onBusyChange) onBusyChange(orderId, false);
    }
  };

  return {
    toggleItem: (orderId, itemId, currentReceived) => {
      if (orderId == null || itemId == null) return Promise.resolve();
      return run(
        orderId,
        () => apiPatch(`${API_BASE}/api/orders/${orderId}/items/${itemId}/received`, { is_received: !currentReceived }),
        'Failed to update item'
      );
    },
    receiveAll: (orderId, nextReceived) => {
      if (orderId == null) return Promise.resolve();
      return run(
        orderId,
        () => apiPatch(`${API_BASE}/api/orders/${orderId}/items/receive-all`, { is_received: nextReceived }),
        'Failed to update items'
      );
    },
    // 🔀 PER-LINE "ORDERED" (2026-10-28) — the step BEFORE receive, through the
    // SAME `run` (busy bracket, error surface, detail-then-list refresh), so
    // there is still one mutation path. `PATCH …/items/:itemId/ordered` with
    // the FLIPPED value; `…/items/order-all` with `next` (false clears only
    // un-received lines, server-side).
    toggleOrdered: (orderId, itemId, currentOrdered) => {
      if (orderId == null || itemId == null) return Promise.resolve();
      return run(
        orderId,
        () => apiPatch(`${API_BASE}/api/orders/${orderId}/items/${itemId}/ordered`, { is_ordered: !currentOrdered }),
        'Failed to update item'
      );
    },
    orderAll: (orderId, nextOrdered) => {
      if (orderId == null) return Promise.resolve();
      return run(
        orderId,
        () => apiPatch(`${API_BASE}/api/orders/${orderId}/items/order-all`, { is_ordered: nextOrdered }),
        'Failed to update items'
      );
    },
  };
}
